import type { Component } from '../router';
import { renderBuildStamp } from '../components/BuildStamp';
import { buildLocaleSwitcher } from '../components/LocaleSwitcher';
import { ApiError } from '../services/api';
import { requestMagicLink, startOAuth, getAuthErrorMessage, type OAuthProvider } from '../services/auth';
import { t } from '../i18n';
import { isValidEmail } from '../services/validation';

type SubmitState = 'idle' | 'sending' | 'sent' | 'error';

const SVG_NS = 'http://www.w3.org/2000/svg';

// Brand marks built as elements (not innerHTML), matching components/icons.ts.
// Google is its fixed four-colour glyph; Apple is a single path filled with
// currentColor so it tracks the button's text colour (white on the dark button
// in light mode, dark on the white button in dark mode).
const BRAND_PATHS: Record<OAuthProvider, { d: string; fill: string }[]> = {
  google: [
    { d: 'M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z', fill: '#4285F4' },
    { d: 'M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z', fill: '#34A853' },
    { d: 'M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z', fill: '#FBBC05' },
    { d: 'M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z', fill: '#EA4335' },
  ],
  apple: [
    { d: 'M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z', fill: 'currentColor' },
  ],
};

function brandMark(provider: OAuthProvider): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '18');
  svg.setAttribute('height', '18');
  svg.setAttribute('aria-hidden', 'true');
  svg.classList.add('btn-oauth__mark');
  for (const { d, fill } of BRAND_PATHS[provider]) {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', d);
    path.setAttribute('fill', fill);
    svg.appendChild(path);
  }
  return svg;
}

function oauthButton(provider: OAuthProvider, label: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `btn btn-oauth btn-oauth--${provider}`;
  btn.dataset.provider = provider;
  btn.appendChild(brandMark(provider));
  const span = document.createElement('span');
  span.className = 'btn-oauth__label';
  span.textContent = label;
  btn.appendChild(span);
  btn.addEventListener('click', () => {
    // A full-page navigation to the provider follows; disable so a second
    // click can't fire a duplicate init while the browser is leaving.
    btn.disabled = true;
    startOAuth(provider);
  });
  return btn;
}

// Machine error code off an ApiError body ({ error, code }), or '' if absent.
function errorCode(err: ApiError): string {
  const body: unknown = err.body;
  if (body && typeof body === 'object' && 'code' in body) {
    const code = (body as { code?: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return '';
}

export function errorCopy(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return t('common.tooManyAttempts');
    // 403 is ambiguous on this endpoint — CSRF lands here too — so branch on
    // the code, not the status.
    if (err.status === 403 && errorCode(err) === 'CAPTCHA_FAILED') {
      return t('login.captchaFailed');
    }
    return t('common.serverError');
  }
  return t('common.serverUnreachable');
}

export class LoginPage implements Component {
  private root: HTMLElement | null = null;
  // Pending keyboard-assist scroll timer, cancelled on unmount so it can never
  // fire against a page that is already gone.
  private scrollAssist: number | null = null;

  mount(host: HTMLElement): void {
    // Start loading reCAPTCHA now, not at submit: the script is async, so a
    // cold-page submit would find window.grecaptcha undefined and send no
    // token. Loading while the user types their email hides the latency.
    // No-ops when no site key is configured. Lazy on purpose (issue 774): the
    // module and the inlined key must not ride the first load, and a chunk
    // that cannot be fetched is the same best-effort miss as a blocked script.
    void import('../services/captcha')
      .then((m) => m.preloadRecaptcha())
      .catch(() => false);

    const section = document.createElement('section');
    section.className = 'login';

    const card = document.createElement('div');
    card.className = 'login__card';

    const brand = document.createElement('div');
    brand.className = 'login__brand';
    const logo = document.createElement('img');
    logo.src = '/brand/logo.png';
    logo.alt = t('common.brand');
    logo.width = 616;
    logo.height = 256;
    brand.appendChild(logo);

    const title = document.createElement('h1');
    title.className = 'login__title';
    title.textContent = t('login.title');

    const sub = document.createElement('p');
    sub.className = 'login__sub';
    sub.textContent = t('login.sub');

    const form = document.createElement('form');
    form.className = 'login__form';
    form.noValidate = true;

    const label = document.createElement('label');
    label.className = 'login__label';
    label.htmlFor = 'login-email';
    label.textContent = t('common.email');

    const input = document.createElement('input');
    input.className = 'login__input';
    input.id = 'login-email';
    input.name = 'email';
    input.type = 'email';
    input.required = true;
    input.placeholder = t('common.emailPlaceholder');
    input.autocomplete = 'email';
    input.spellcheck = false;
    input.autocapitalize = 'none';
    input.enterKeyHint = 'send';
    input.setAttribute('autocorrect', 'off');

    const submit = document.createElement('button');
    submit.className = 'btn btn--primary login__submit';
    submit.type = 'submit';
    submit.dataset.state = 'idle';
    const submitLabel = document.createElement('span');
    submitLabel.className = 'login__submit-label';
    submitLabel.textContent = t('login.send');
    submit.appendChild(submitLabel);

    const status = document.createElement('p');
    status.className = 'login__status';
    status.dataset.visible = 'false';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    const footnote = document.createElement('p');
    footnote.className = 'login__footnote';
    footnote.textContent = t('login.footnote');

    const setState = (next: SubmitState, message?: string, tone?: 'success' | 'error'): void => {
      submit.dataset.state = next;
      // Lock the button in 'sending' AND 'sent' so a second click can't
      // fire a duplicate magic-link send. 'error' stays clickable so the
      // user can retry without reloading.
      submit.disabled = next === 'sending' || next === 'sent';
      submitLabel.textContent = next === 'sent' ? t('login.checkInbox') : t('login.send');
      if (message) {
        status.textContent = message;
        status.dataset.visible = 'true';
        if (tone) status.dataset.tone = tone;
        else delete status.dataset.tone;
      } else {
        status.dataset.visible = 'false';
      }
    };

    // When the user edits the email field after a 'sent' or 'error',
    // reset to idle so they can re-submit (e.g. a typo correction).
    // No timeout-based revert: that's what produced the "second click =
    // duplicate email" bug.
    input.addEventListener('input', () => {
      const s = submit.dataset.state;
      if (s === 'sent' || s === 'error') {
        setState('idle');
      }
    });

    // Keyboard assist for short viewports (SE-class phones): once the
    // on-screen keyboard has settled, nudge the field to mid-viewport so
    // it and the submit button clear the keyboard + accessory bar. The
    // activeElement guard skips the scroll if focus moved on before the
    // timer fired.
    //
    // The timer is tracked and cancelled on unmount: leaving it pending
    // outlives the page (navigate away mid-focus and it still fires), and the
    // activeElement guard is scoped to the input's own document for the same
    // reason (see below), so it degrades quietly instead of throwing.
    input.addEventListener('focus', () => {
      this.clearScrollAssist();
      // The guard reads the input's OWN document, captured now, not the global
      // `document` at fire time. Cancelling on unmount is the primary defence,
      // but it only helps callers that unmount — and mount() auto-focuses this
      // field, so anything that drops the page without unmounting leaves the
      // timer pending. Reaching for a global that no longer exists then throws
      // the very ReferenceError this guard was meant to prevent.
      const doc = input.ownerDocument;
      this.scrollAssist = window.setTimeout(() => {
        this.scrollAssist = null;
        if (doc.activeElement === input && typeof input.scrollIntoView === 'function') {
          input.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 300);
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      // Belt-and-suspenders: if the button is already disabled for any
      // reason, ignore the submit (covers Enter-key paths around the
      // disabled state).
      if (submit.disabled) return;
      // The form sets noValidate, so nothing else stops an empty or malformed
      // address: without this the page would report "check your inbox" for
      // input that never produced a valid request. Enumeration policy is
      // untouched — a validly SHAPED address still gets the same generic
      // response whether or not it is registered.
      const email = input.value.trim();
      if (!isValidEmail(email)) {
        setState('error', t('common.emailInvalid'), 'error');
        return;
      }
      setState('sending', t('common.sending'));
      try {
        await requestMagicLink(email);
        // Server returns 200 for any email per enumeration policy. The
        // button stays locked in 'sent' until the user edits the email
        // — see the input listener above.
        setState('sent', t('login.sentStatus'), 'success');
      } catch (err) {
        // Enumeration policy means happy-path /api/auth/magic-link returns
        // 200 for any email — but CSRF (403), rate limit (429), and server
        // faults (5xx) all still surface as ApiError. Reporting all of them
        // as "could not reach the server" sent #77 down the wrong path.
        setState('error', errorCopy(err), 'error');
      }
    });

    form.append(label, input, submit, status, footnote);

    // OAuth alternative below the magic-link form.
    const divider = document.createElement('div');
    divider.className = 'login__divider';
    divider.setAttribute('role', 'separator');
    const dividerLabel = document.createElement('span');
    dividerLabel.textContent = t('login.or');
    divider.appendChild(dividerLabel);

    const oauth = document.createElement('div');
    oauth.className = 'login__oauth';
    oauth.append(
      oauthButton('apple', t('login.continueApple')),
      oauthButton('google', t('login.continueGoogle')),
    );

    // Lost-passkey recovery entry point. A logged-out user who can't
    // complete a sensitive op because their passkey is gone starts here; the
    // router intercepts the anchor for client-side navigation.
    const recovery = document.createElement('p');
    recovery.className = 'login__recovery';
    const recoveryLink = document.createElement('a');
    recoveryLink.href = '/recovery';
    recoveryLink.textContent = t('common.lostPasskey');
    recovery.appendChild(recoveryLink);

    const cardChildren: HTMLElement[] = [brand, title, sub];

    const search = new URLSearchParams(window.location.search);

    // A forced re-auth (device-class change on refresh) lands here as
    // /login?reason=security (app.ts rewrites the boot URL). Show a calm,
    // informational "signed out for security" notice above the form so the user
    // knows why they're back at sign-in — never a silent bounce (FE-013.1). The
    // copy is generic by design: it discloses no device or session detail
    // (FE-013.2). No timers or listeners, so nothing to tear down on unmount.
    if (search.get('reason') === 'security') {
      const notice = document.createElement('p');
      notice.className = 'login__status login__notice';
      notice.dataset.visible = 'true';
      notice.setAttribute('role', 'status');
      notice.textContent = t('login.securitySignout');
      cardChildren.push(notice);
    }

    // A self role change ends the session and lands here as /login?reason=role-changed
    // (forceReauth, issue 200). Show the same calm, informational notice styling as the
    // security sign-out so the user knows the session ended because their role changed
    // and that re-signing in will carry the new permissions — never a silent bounce.
    if (search.get('reason') === 'role-changed') {
      const notice = document.createElement('p');
      notice.className = 'login__status login__notice';
      notice.dataset.visible = 'true';
      notice.setAttribute('role', 'status');
      notice.textContent = t('login.roleChangedSignout');
      cardChildren.push(notice);
    }

    // An access token that expired under an open page and could not be renewed
    // (the refresh cookie itself is gone — issue 712) lands here as
    // /login?reason=session-expired. Same calm notice: the session ended because
    // it timed out, signing in again continues — never a silent bounce.
    if (search.get('reason') === 'session-expired') {
      const notice = document.createElement('p');
      notice.className = 'login__status login__notice';
      notice.dataset.visible = 'true';
      notice.setAttribute('role', 'status');
      notice.textContent = t('login.sessionExpiredSignout');
      cardChildren.push(notice);
    }

    // An OAuth callback failure is forwarded here as /login?error=CODE (app.ts
    // rewrites the provider's ?auth=error&code=CODE landing). Surface it above
    // the form, where the "use a magic link instead" copy points at the form.
    const errorCode = search.get('error');
    if (errorCode) {
      const banner = document.createElement('p');
      banner.className = 'login__status login__alert';
      banner.dataset.visible = 'true';
      banner.dataset.tone = 'error';
      banner.setAttribute('role', 'alert');
      banner.textContent = getAuthErrorMessage(errorCode);
      cardChildren.push(banner);
    }

    cardChildren.push(form, divider, oauth, recovery, buildLocaleSwitcher());
    card.append(...cardChildren);
    renderBuildStamp(card);
    section.appendChild(card);
    host.appendChild(section);
    this.root = section;

    // Focus email field on mount so keyboard users can start typing.
    queueMicrotask(() => input.focus());
  }

  unmount(): void {
    this.clearScrollAssist();
    this.root?.remove();
    this.root = null;
  }

  private clearScrollAssist(): void {
    if (this.scrollAssist !== null) {
      window.clearTimeout(this.scrollAssist);
      this.scrollAssist = null;
    }
  }
}
